import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, BACKEND_URL } from "@/lib/api";
import Layout from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Search, Filter, Building2, Globe, FileDown, Handshake, ArrowRight } from "lucide-react";
import { FadeUp } from "@/components/Motion";

export default function Directory() {
  const [exhibitors, setExhibitors] = useState([]);
  const [meta, setMeta] = useState({ industries: [], certifications: [] });
  const [q, setQ] = useState("");
  const [industry, setIndustry] = useState("");
  const [cert, setCert] = useState("");

  const fetchList = async () => {
    const params = {};
    if (q) params.q = q;
    if (industry) params.industry = industry;
    if (cert) params.certification = cert;
    const { data } = await api.get("/exhibitors", { params });
    setExhibitors(data.exhibitors);
  };

  useEffect(() => { api.get("/event").then(({ data }) => setMeta({ industries: data.industries, certifications: data.certifications })); }, []);
  useEffect(() => { fetchList(); /* eslint-disable-line */ }, [industry, cert]);
  useEffect(() => { const t = setTimeout(fetchList, 250); return () => clearTimeout(t); /* eslint-disable-line */ }, [q]);

  return (
    <Layout>
      {/* Hero */}
      <section className="border-b border-[hsl(var(--line))]">
        <div className="container-summit py-14 md:py-20">
          <FadeUp className="overline mb-4"><span className="accent-rule" />Exhibitor Directory</FadeUp>
          <FadeUp delay={0.05} className="flex items-end justify-between gap-6 flex-wrap">
            <h1 className="serif text-4xl sm:text-6xl">Meet the suppliers.</h1>
            <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">{exhibitors.length} listed</div>
          </FadeUp>

          <FadeUp delay={0.1} className="mt-10 grid md:grid-cols-[1fr_auto_auto] gap-3">
            <div className="relative">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input data-testid="dir-search" placeholder="Search company, products, services…" value={q}
                onChange={(e) => setQ(e.target.value)} className="h-12 rounded-none pl-11" />
            </div>
            <Select value={industry} onChange={setIndustry} options={meta.industries} placeholder="All Industries" testId="dir-industry" />
            <Select value={cert} onChange={setCert} options={meta.certifications} placeholder="All Certifications" testId="dir-cert" />
          </FadeUp>
        </div>
      </section>

      <section className="container-summit py-12">
        {exhibitors.length === 0 ? (
          <div className="card-flat p-10 text-center">
            <Building2 size={28} className="mx-auto text-zinc-400" strokeWidth={1.2} />
            <p className="mt-4 text-sm text-zinc-500">No approved exhibitors yet.</p>
          </div>
        ) : (
          <motion.div initial="hidden" animate="show"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.04 } } }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {exhibitors.map((e) => (
              <motion.article key={e.id} variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.45 } } }}
                className="group bg-white border border-zinc-200 hover:border-black transition-colors p-6 md:p-7 flex flex-col gap-5"
                data-testid={`dir-card-${e.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="h-14 flex items-center">
                    {e.logo_file_id ? (
                      <img src={`${BACKEND_URL}/api/files/${e.logo_file_id}`} alt={e.company_name} className="max-h-14 w-auto object-contain" />
                    ) : (
                      <div className="serif text-xl">{e.company_name.slice(0, 16)}</div>
                    )}
                  </div>
                  {e.booth_number && <span className="pill bg-black text-white border-black text-[10px]">Booth {e.booth_number}</span>}
                </div>

                <div>
                  <div className="serif text-2xl leading-snug">{e.company_name}</div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mt-1">{e.industry}</div>
                </div>

                {(e.certifications || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {e.certifications.slice(0, 4).map((c) => <span key={c} className="pill text-[10px]">{c}</span>)}
                  </div>
                )}

                <p className="text-sm text-zinc-600 leading-relaxed line-clamp-3">{e.description}</p>

                <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 pt-2 border-t border-zinc-100">
                  {e.primary_contact || "Primary Contact"}
                </div>

                <div className="mt-auto grid grid-cols-2 gap-2">
                  <Link to={`/directory/${e.id}`} className="btn-outline-black h-11 text-xs" data-testid={`dir-view-${e.id}`}>
                    View Profile
                  </Link>
                  <Link to="/procurement" state={{ company: e.company_name }} className="btn-black h-11 text-xs" data-testid={`dir-meet-${e.id}`}>
                    <Handshake size={12} /> Request Meeting
                  </Link>
                </div>
                <div className="flex flex-wrap gap-3 text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                  {e.website && (
                    <a href={e.website.startsWith("http") ? e.website : `https://${e.website}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-black">
                      <Globe size={12} /> Website
                    </a>
                  )}
                  {e.capability_statement_file_id && (
                    <a href={`${BACKEND_URL}/api/files/${e.capability_statement_file_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-black">
                      <FileDown size={12} /> Capability Statement
                    </a>
                  )}
                </div>
              </motion.article>
            ))}
          </motion.div>
        )}
      </section>
    </Layout>
  );
}

function Select({ value, onChange, options, placeholder, testId }) {
  return (
    <div className="relative">
      <Filter size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
      <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}
        className="h-12 pl-11 pr-8 border border-zinc-200 bg-white text-sm uppercase tracking-[0.12em] focus:outline-none focus:border-black w-full md:w-56">
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
